/**
 * Device Replacement Service
 * Service สำหรับจัดการ API การเปลี่ยนอุปกรณ์/SIM
 *
 * รองรับ 3 โหมด:
 * - DEVICE_ONLY: เปลี่ยนเฉพาะอุปกรณ์
 * - SIM_ONLY: เปลี่ยนเฉพาะ SIM
 * - BOTH: เปลี่ยนทั้งอุปกรณ์และ SIM
 */
import ApiWithLogging from '../utils/apiWithLogging';

// Enums
export type ReplacementType = 'DEVICE_ONLY' | 'SIM_ONLY' | 'BOTH';
export type DeviceCondition = 'DAMAGED' | 'LOST' | 'MAINTENANCE';
export type SimCondition = 'SIM_EXPIRED' | 'SIM_FAULTY' | 'SIM_CHANGE_OPERATOR' | 'SIM_UPGRADE'
  | 'SIM_DATA_ISSUE' | 'SIM_NORMAL_WITH_DEVICE';

// Icon types for replacement (used by components to render appropriate icons)
export type ReplacementIconType = 'device' | 'sim' | 'both';

// Constants for UI
export const REPLACEMENT_TYPES = [
  { value: 'DEVICE_ONLY' as ReplacementType, label: 'เปลี่ยนเฉพาะอุปกรณ์', iconType: 'device' as ReplacementIconType },
  { value: 'SIM_ONLY' as ReplacementType, label: 'เปลี่ยนเฉพาะ SIM', iconType: 'sim' as ReplacementIconType },
  { value: 'BOTH' as ReplacementType, label: 'เปลี่ยนทั้งอุปกรณ์และ SIM', iconType: 'both' as ReplacementIconType },
];

export const DEVICE_CONDITIONS = [
  { value: 'DAMAGED' as DeviceCondition, label: 'ชำรุด' },
  { value: 'LOST' as DeviceCondition, label: 'สูญหาย' },
  { value: 'MAINTENANCE' as DeviceCondition, label: 'ซ่อมบำรุง' },
];

export const SIM_CONDITIONS = [
  { value: 'SIM_EXPIRED' as SimCondition, label: 'SIM หมดอายุ' },
  { value: 'SIM_FAULTY' as SimCondition, label: 'SIM เสีย/ใช้งานไม่ได้' },
  { value: 'SIM_CHANGE_OPERATOR' as SimCondition, label: 'เปลี่ยนค่ายมือถือ' },
  { value: 'SIM_UPGRADE' as SimCondition, label: 'อัพเกรด Package' },
  { value: 'SIM_DATA_ISSUE' as SimCondition, label: 'ปัญหาสัญญาณ/เน็ต' },
  // ทิวากร 2026-09-07: ส่งเครื่องใหม่ไปพร้อมซิมใหม่ทั้งชุด ซิมเดิมไม่ได้เสีย
  // → ระบบพักซิมเดิมไว้ (ไม่ได้ใช้งาน) แล้วคืนเข้าคลังอัตโนมัติเมื่อบันทึกรับคืนในใบตามเก็บ
  {
    value: 'SIM_NORMAL_WITH_DEVICE' as SimCondition,
    label: 'SIM เดิมปกติ — ส่งซิมใหม่เปลี่ยนพร้อมเครื่องหลัก',
  },
];

// Types
export interface DeviceSimple {
  id: number;
  code: string | null;
  serial_number: string | null;
  device_type_name: string | null;
}

export interface SimSimple {
  id: number;
  phone_number: string | null;
  operator: string | null;
}

export interface UserSimple {
  id: number;
  username: string | null;
  full_name: string | null;
}

export interface DeviceReplacementCreate {
  replacement_type?: ReplacementType;  // default: DEVICE_ONLY
  // Device fields (required for DEVICE_ONLY or BOTH)
  new_device_id?: number | null;
  old_device_condition?: DeviceCondition | null;
  // ISSUE-S164-REPLRACE: optimistic-lock guard — device_id ที่แสดงใน dialog; mismatch → 409
  expected_old_device_id?: number | null;
  // SIM fields (required for SIM_ONLY or BOTH)
  new_sim_id?: number | null;
  old_sim_condition?: SimCondition | null;
  // AnyDesk ของเครื่องใหม่ (มีผลเฉพาะ DEVICE_ONLY/BOTH) — null/ไม่ส่ง = backend ล้างค่าเก่าของเครื่องเดิมออก
  new_anydesk_id?: string | null;
  // Common fields
  reason: string;
  notes?: string | null;
  // ผูกการเปลี่ยนอุปกรณ์นี้เข้ากับเคสซ่อมบำรุง (ถ้าเปลี่ยนจากหน้ารายละเอียดเคสซ่อม)
  maintenance_id?: number | null;
}

export interface DeviceReplacementResponse {
  id: number;
  rental_id: number;
  rental_item_id: number;
  replacement_type: ReplacementType;  // NEW
  old_device_id: number | null;
  new_device_id: number | null;
  old_device: DeviceSimple | null;
  new_device: DeviceSimple | null;
  old_device_condition: string | null;
  old_sim_id: number | null;
  new_sim_id: number | null;
  old_sim: SimSimple | null;
  new_sim: SimSimple | null;
  old_sim_condition: string | null;  // NEW
  old_anydesk_id?: string | null;
  new_anydesk_id?: string | null;
  reason: string;
  replacement_date: string;
  replaced_by_user_id: number;
  replaced_by: UserSimple | null;
  notes: string | null;
  created_at: string;
  // เคสซ่อมบำรุงที่ผูกการเปลี่ยนนี้ไว้ (ถ้ามี)
  maintenance_id?: number | null;
}

export interface DeviceReplacementHistoryItem {
  id: number;
  replacement_date: string;
  replacement_type: ReplacementType;  // NEW
  old_device_code: string | null;
  old_device_serial: string | null;
  new_device_code: string | null;
  new_device_serial: string | null;
  old_device_condition: string | null;
  old_sim_number: string | null;
  new_sim_number: string | null;
  old_sim_condition: string | null;  // NEW
  old_anydesk_id?: string | null;
  new_anydesk_id?: string | null;
  reason: string;
  notes: string | null;
  replaced_by_name: string | null;
  created_at: string;
}

// ========== Device Replacement Center Types ==========

export interface DeviceForReplacementItem {
  device_id: number;
  device_code: string;
  serial_number: string | null;
  imei: string | null;
  model: string | null;
  device_type_id: number | null;
  device_type_name: string | null;
  device_status: string;
  rental_id: number;
  rental_code: string;
  rental_item_id: number;
  rental_status: string;
  company_id: number | null;
  company_name: string | null;
  site_id: number | null;
  site_name: string | null;
  // SIM info สำหรับ SIM replacement support
  sim_id: number | null;
  sim_phone_number: string | null;
  sim_operator: string | null;
  sim_expiry_date: string | null;
  // AnyDesk ปัจจุบันของ rental item ที่เครื่องนี้อยู่ (23 ก.ย. 2026 — backend ส่ง `current_anydesk_id`)
  current_anydesk_id?: string | null;
  /** @deprecated ชื่อเก่า — ใช้ current_anydesk_id */
  anydesk_id?: string | null;
  replacement_count: number;
}

export interface DeviceSearchForReplacementResponse {
  items: DeviceForReplacementItem[];
  total: number;
  /** KD3 S2382: ยังมีผลที่ตรงเกินจำนวนที่แสดง — ให้ผู้ใช้พิมพ์คำค้นให้แคบลง */
  has_more?: boolean;
}

export interface AllReplacementHistoryItem {
  id: number;
  replacement_date: string;
  replacement_type: ReplacementType;  // NEW
  old_device_id: number | null;
  old_device_code: string | null;
  old_device_serial: string | null;
  new_device_id: number | null;
  new_device_code: string | null;
  new_device_serial: string | null;
  old_device_condition: string | null;
  device_type_name: string | null;
  old_sim_id: number | null;
  old_sim_number: string | null;
  new_sim_id: number | null;
  new_sim_number: string | null;
  old_sim_condition: string | null;  // NEW
  old_anydesk_id?: string | null;
  new_anydesk_id?: string | null;
  rental_id: number;
  rental_code: string;
  rental_item_id: number;
  company_id: number | null;
  company_name: string | null;
  site_id: number | null;
  site_name: string | null;
  reason: string;
  notes: string | null;
  replaced_by_user_id: number;
  replaced_by_name: string | null;
  created_at: string;
  // เคสซ่อมบำรุงที่ผูกการเปลี่ยนนี้ไว้ (ถ้ามี)
  maintenance_id?: number | null;
}

export interface AllReplacementHistoryResponse {
  items: AllReplacementHistoryItem[];
  total: number;
  page: number;
  limit: number;
}

export interface AllHistoryFilters {
  page?: number;
  limit?: number;
  date_from?: string;
  date_to?: string;
  company_id?: number;
  // สภาพ "ของเดิม" เป็นคนละคอลัมน์ตามชนิดของ (ค่าไม่ทับกัน: DAMAGED/LOST/MAINTENANCE vs SIM_*)
  // จอส่งมาทีละตัวจาก dropdown เดียวที่แบ่งกลุ่ม — ส่งพร้อมกันได้ backend ตีความเป็น AND
  old_device_condition?: string;
  old_sim_condition?: string;
  /** โครงการของสัญญา — หน้ารายละเอียดโครงการ (FB-2026-0009) */
  site_id?: number;
}

// Service
const deviceReplacementService = {
  /**
   * เปลี่ยนอุปกรณ์ในรายการเช่า
   */
  async replaceDevice(
    rentalItemId: number,
    data: DeviceReplacementCreate
  ): Promise<DeviceReplacementResponse> {
    const response = await ApiWithLogging.post<DeviceReplacementResponse>(
      `/device-replacements/rental-items/${rentalItemId}/replace-device`,
      data
    );
    return response;
  },

  /**
   * ดึงประวัติการเปลี่ยนอุปกรณ์ของรายการเช่า
   */
  async getRentalItemReplacementHistory(
    rentalItemId: number,
    skip: number = 0,
    limit: number = 100
  ): Promise<DeviceReplacementHistoryItem[]> {
    const response = await ApiWithLogging.get<DeviceReplacementHistoryItem[]>(
      `/device-replacements/rental-items/${rentalItemId}/replacement-history`,
      { params: { skip, limit } }
    );
    return response;
  },

  /**
   * ดึงประวัติการเปลี่ยนอุปกรณ์ทั้งสัญญาเช่า
   */
  async getRentalReplacementHistory(
    rentalId: number,
    skip: number = 0,
    limit: number = 100
  ): Promise<DeviceReplacementHistoryItem[]> {
    const response = await ApiWithLogging.get<DeviceReplacementHistoryItem[]>(
      `/device-replacements/rentals/${rentalId}/replacement-history`,
      { params: { skip, limit } }
    );
    return response;
  },

  /**
   * ดึงประวัติการเปลี่ยนอุปกรณ์ของ device
   */
  async getDeviceReplacementHistory(
    deviceId: number,
    skip: number = 0,
    limit: number = 100
  ): Promise<DeviceReplacementHistoryItem[]> {
    const response = await ApiWithLogging.get<DeviceReplacementHistoryItem[]>(
      `/device-replacements/devices/${deviceId}/replacement-history`,
      { params: { skip, limit } }
    );
    return response;
  },

  /**
   * Export ประวัติการเปลี่ยนอุปกรณ์เป็น Excel
   */
  async exportToExcel(filters: Omit<AllHistoryFilters, 'page' | 'limit'> = {}): Promise<Blob> {
    const params: Record<string, string | number | undefined> = {};
    if (filters.date_from) params.date_from = filters.date_from;
    if (filters.date_to) params.date_to = filters.date_to;
    if (filters.company_id) params.company_id = filters.company_id;
    if (filters.old_device_condition) params.old_device_condition = filters.old_device_condition;
    // export ต้องส่ง filter ชุดเดียวกับ list ทุกตัว (bug class export-filter-parity)
    if (filters.old_sim_condition) params.old_sim_condition = filters.old_sim_condition;

    const response = await ApiWithLogging.get<Blob>(
      '/device-replacements/export/excel',
      { params, responseType: 'blob' },
      {
        service: 'deviceReplacementService',
        action: 'export_device_replacements_excel',
        entityType: 'device_replacement',
      }
    );
    return response;
  },

  /**
   * ดึงรายละเอียดการเปลี่ยนอุปกรณ์
   */
  async getReplacementDetail(
    replacementId: number
  ): Promise<DeviceReplacementResponse> {
    const response = await ApiWithLogging.get<DeviceReplacementResponse>(
      `/device-replacements/${replacementId}`
    );
    return response;
  },

  // ========== Device Replacement Center Functions ==========

  /**
   * ค้นหาอุปกรณ์สำหรับเปลี่ยน (Device Replacement Center)
   */
  async searchDevicesForReplacement(
    query: string,
    limit: number = 10
  ): Promise<DeviceSearchForReplacementResponse> {
    const response = await ApiWithLogging.get<DeviceSearchForReplacementResponse>(
      '/device-replacements/search-devices',
      { params: { q: query, limit } }
    );
    return response;
  },

  /**
   * ดึงประวัติการเปลี่ยนอุปกรณ์ทั้งระบบ (Device Replacement Center)
   */
  async getAllReplacementHistory(
    filters: AllHistoryFilters = {}
  ): Promise<AllReplacementHistoryResponse> {
    const params: Record<string, string | number | undefined> = {
      page: filters.page || 1,
      limit: filters.limit || 20,
    };

    if (filters.date_from) params.date_from = filters.date_from;
    if (filters.date_to) params.date_to = filters.date_to;
    if (filters.company_id) params.company_id = filters.company_id;
    if (filters.old_device_condition) params.old_device_condition = filters.old_device_condition;
    if (filters.old_sim_condition) params.old_sim_condition = filters.old_sim_condition;
    if (filters.site_id) params.site_id = filters.site_id;

    const response = await ApiWithLogging.get<AllReplacementHistoryResponse>(
      '/device-replacements/all-history',
      { params }
    );
    return response;
  },
};

export default deviceReplacementService;
